import React, { Component } from "react";
import PropTypes from "prop-types";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { isDefined, identity, noop, functor } from "../utils";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class VolumeTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const { onClick, fontFamily, fontSize, labelFill, bgFill, className } =
      this.props;
    const {
      xDisplayFormat,
      yDisplayFormat,
      xLabel,
      yLabel,
      xAccessor,
      yAccessor,
    } = this.props;
    const { displayValuesFor, upColor, downColor, volumePathConfig } =
      this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;
    const currentItem = displayValuesFor(this.props, moreProps);

    const xDisplayValue =
      isDefined(currentItem) && isDefined(xAccessor(currentItem))
        ? xDisplayFormat(xAccessor(currentItem))
        : "n/a";

    // Determine dynamic label based on volumePath (NEW)
    let dynamicLabel = yLabel;
    if (typeof yLabel === "function") {
      dynamicLabel = yLabel(volumePathConfig);
    } else if (volumePathConfig?.volumePath) {
      // Map volumePath to display label
      const labelMap = {
        volume: "Volume (Qtd)",
        tickCount: "Volume (Neg)",
        financialVolume: "Volume (Fin)",
      };
      dynamicLabel = labelMap[volumePathConfig.volumePath] || yLabel;
    }

    // Get volume data and determine dynamic color
    const volumeData =
      isDefined(currentItem) && isDefined(yAccessor(currentItem))
        ? yAccessor(currentItem)
        : null;

    // Extract volume number from volume data object
    let volumeNumber = null;
    if (volumeData) {
      if (typeof volumeData === "number") {
        volumeNumber = volumeData;
      } else if (volumeData && typeof volumeData.volume === "number") {
        volumeNumber = volumeData.volume;
      }
    }

    const yDisplayValue =
      volumeNumber !== null ? yDisplayFormat(volumeNumber) : "n/a";

    // Dynamic color based on volume direction
    const direction = volumeData?.direction;
    const dynamicValueFill = direction === "up" ? upColor : downColor;

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    // Calculate text width for background (use dynamicLabel)
    const fullText = `${xLabel ? xLabel + ": " + xDisplayValue + " " : ""}${dynamicLabel}: ${yDisplayValue}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgWidth = textWidth + TOOLTIP_BG_PADDING * 2;

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-volume"
      >
        {/* Background rectangle */}
        <rect
          x={-TOOLTIP_BG_PADDING}
          y={-5}
          width={bgWidth}
          height={TOOLTIP_BG_HEIGHT}
          fill={bgFill || "#1f2937"}
          rx={6}
          ry={6}
        />
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          {xLabel ? (
            <ToolTipTSpanLabel
              x={0}
              dy="5"
              fill={labelFill}
            >{`${xLabel}: `}</ToolTipTSpanLabel>
          ) : null}
          {xLabel ? (
            <tspan fill={dynamicValueFill}>{`${xDisplayValue} `}</tspan>
          ) : null}
          <ToolTipTSpanLabel
            fill={labelFill}
          >{`${dynamicLabel}: `}</ToolTipTSpanLabel>
          <tspan fill={dynamicValueFill}>{yDisplayValue}</tspan>
        </ToolTipText>
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

VolumeTooltip.propTypes = {
  xDisplayFormat: PropTypes.func,
  yDisplayFormat: PropTypes.func.isRequired,
  xLabel: PropTypes.string,
  yLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  labelFill: PropTypes.string.isRequired,
  bgFill: PropTypes.string,
  upColor: PropTypes.string.isRequired,
  downColor: PropTypes.string.isRequired,
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  onClick: PropTypes.func,
  displayValuesFor: PropTypes.func,
  xAccessor: PropTypes.func,
  yAccessor: PropTypes.func,
  volumePathConfig: PropTypes.object, // NEW: Indicator config for dynamic labels
};

VolumeTooltip.defaultProps = {
  origin: [0, 0],
  labelFill: "#4682B4",
  upColor: "#6BA583",
  downColor: "#FF0000",
  yLabel: (config) => {
    // Dynamic label based on volumePath selection (NEW)
    if (!config?.volumePath) return "Volume";

    const labelMap = {
      volume: "Volume (Qtd)",
      tickCount: "Volume (Neg)",
      financialVolume: "Volume (Fin)",
    };
    return labelMap[config.volumePath] || "Volume";
  },
  yDisplayFormat: (d) => {
    // Handle both direct numbers and objects with volume property
    const volume = typeof d === "number" ? d : d?.volume || 0;
    // Format with Portuguese locale (dot for thousands, comma for decimals)
    return volume.toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  displayValuesFor: displayValuesFor,
  xAccessor: noop,
  yAccessor: identity,
  className: "react-stockcharts-tooltip",
  volumePathConfig: null, // NEW: Will be passed from ChartTooltips
};

export default VolumeTooltip;
